import { MAX_MESSAGE_PATHS_PER_CALL } from "./workspaceActions";

/**
 * What a visualizer proposal will do, in the terms the approval card shows.
 *
 * The seven visualizer tools differ only in which of these they carry, so
 * they share one preview rather than each restating the same card. Each field
 * is optional and rendered only when present.
 */
export interface VisualizeProposal {
  /** What the tool will do, as a phrase: "Open a Plot panel". */
  title: string;
  /** Accessible name for the preview region. */
  label: string;
  panelId?: string;
  messagePathIds?: string[];
  topicId?: string;
  fileId?: string;
  layoutId?: string;
  timestampNs?: number;
  /** Why this proposal cannot succeed. Rendered instead of the card. */
  problem?: string;
}

/**
 * A string field of the proposal, or `undefined` if the model sent something
 * else. The visualizer tools declare no `validateInput`, so a preview reads
 * params that nothing has checked.
 */
export function proposedString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/** As {@link proposedString}, for the id lists the panel tools take. */
export function proposedStringList(value: unknown): string[] | undefined {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined;
  }
  const entries: unknown[] = value;
  return entries.every((entry) => typeof entry === "string")
    ? entries
    : undefined;
}

/** A finite timestamp in nanoseconds, or `undefined`. */
export function proposedTimestampNs(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

/**
 * The reason a batch of message paths would be refused, if it would be.
 *
 * `execute` enforces the same ceiling, so surfacing it here keeps the card
 * from offering Approve over a call that returns an error.
 */
export function messagePathBatchProblem(
  ids: string[] | undefined,
): string | undefined {
  if (ids === undefined) {
    return "No message paths were named.";
  }
  if (ids.length > MAX_MESSAGE_PATHS_PER_CALL) {
    return `${ids.length} message paths were named, but at most ${MAX_MESSAGE_PATHS_PER_CALL} can be used in one call.`;
  }
  return undefined;
}
